import { useMemo, useState } from 'react';
import type { ResourceType, Technology } from '@/hooks/useResourceOptions';

interface UseCapabilitySearchParams {
  types: ResourceType[];
  technologies: Technology[];
  selectedTypeId: string;
}

export function useCapabilitySearch({ types, technologies, selectedTypeId }: UseCapabilitySearchParams) {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const isSearching = normalizedSearch.length > 0;

  const technologiesMatchingSearch = useMemo(() => {
    if (!isSearching) {
      return technologies;
    }

    return technologies.filter((technology) => {
      const nameMatch = technology.name.toLowerCase().includes(normalizedSearch);
      const descriptionMatch = (technology.description ?? '').toLowerCase().includes(normalizedSearch);
      return nameMatch || descriptionMatch;
    });
  }, [technologies, normalizedSearch, isSearching]);

  const visibleTypes = useMemo(() => {
    if (!isSearching) {
      return types;
    }

    const matchingTypeIds = new Set(technologiesMatchingSearch.map((technology) => technology.resourceTypeId));
    return types.filter((type) => matchingTypeIds.has(type.id));
  }, [types, isSearching, technologiesMatchingSearch]);

  const visibleTechnologies = useMemo(() => {
    if (!visibleTypes.length) {
      return [] as Technology[];
    }

    if (isSearching) {
      if (!selectedTypeId) {
        return technologiesMatchingSearch;
      }

      return technologiesMatchingSearch.filter((technology) => technology.resourceTypeId === selectedTypeId);
    }

    if (!selectedTypeId) {
      return [] as Technology[];
    }

    return technologies.filter((technology) => technology.resourceTypeId === selectedTypeId);
  }, [visibleTypes, isSearching, selectedTypeId, technologiesMatchingSearch, technologies]);

  return {
    searchTerm,
    setSearchTerm,
    isSearching,
    visibleTypes,
    visibleTechnologies,
  };
}

